import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Alert,
  Drawer,
  Divider
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    start_date: null,
    end_date: null,
    user_id: '',
    individual_id: '',
    location_id: '',
    action_type: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [individuals, setIndividuals] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchInitialData();
    fetchLogs();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [indResponse, locResponse] = await Promise.all([
        fetchWithAuth('/api/individuals'),
        fetchWithAuth('/api/locations')
      ]);

      if (indResponse.ok && locResponse.ok) {
        const indData = await indResponse.json();
        const locData = await locResponse.json();
        setIndividuals(indData);
        setLocations(locData);
      }
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date.toISOString());
      if (filters.end_date) params.append('end_date', filters.end_date.toISOString());
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.individual_id) params.append('individual_id', filters.individual_id);
      if (filters.location_id) params.append('location_id', filters.location_id);

      const response = await fetchWithAuth(`/api/audit-logs?${params}`);

      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      } else if (response.status === 403) {
        setError('You do not have permission to view audit logs');
      } else {
        setError('Failed to fetch audit logs');
      }
    } catch (err) {
      setError('Error loading audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCard = (logId) => {
    setExpandedCards(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Individual', 'Location', 'Form Type', 'Details'],
      ...logs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.username,
        log.action,
        log.individual_name || '',
        log.location_name || '',
        log.form_type || '',
        log.new_value || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getActionIcon = (action) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('create') || lowerAction.includes('add')) {
      return <AddIcon />;
    }
    if (lowerAction.includes('update') || lowerAction.includes('edit')) {
      return <EditIcon />;
    }
    if (lowerAction.includes('delete') || lowerAction.includes('deactivate')) {
      return <DeleteIcon />;
    }
    if (lowerAction.includes('complete')) {
      return <CheckCircleIcon />;
    }
    return <EventIcon />;
  };

  const getActionColor = (action) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('create') || lowerAction.includes('add')) return 'success';
    if (lowerAction.includes('update') || lowerAction.includes('edit')) return 'warning';
    if (lowerAction.includes('delete') || lowerAction.includes('deactivate')) return 'error';
    if (lowerAction.includes('complete')) return 'info';
    return 'default';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Group logs by date
  const groupLogsByDate = (logs) => {
    const grouped = {};
    logs.forEach(log => {
      const dateKey = new Date(log.timestamp).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(log);
    });
    return Object.entries(grouped).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  };

  const groupedLogs = groupLogsByDate(logs);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon /> Audit Log
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={showFilters ? <CloseIcon /> : <FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportLogs}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={showFilters}
        onClose={() => setShowFilters(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 320,
            p: 3
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Filters</Typography>
          <IconButton onClick={() => setShowFilters(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>Start Date</Typography>
            <DatePicker
              selected={filters.start_date}
              onChange={(date) => setFilters({ ...filters, start_date: date })}
              placeholderText="Select start date"
              customInput={<TextField fullWidth size="small" />}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>End Date</Typography>
            <DatePicker
              selected={filters.end_date}
              onChange={(date) => setFilters({ ...filters, end_date: date })}
              placeholderText="Select end date"
              customInput={<TextField fullWidth size="small" />}
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Individual</InputLabel>
            <Select
              value={filters.individual_id}
              onChange={(e) => setFilters({ ...filters, individual_id: e.target.value })}
              label="Individual"
            >
              <MenuItem value="">All</MenuItem>
              {individuals.map(ind => (
                <MenuItem key={ind.id} value={ind.id}>{ind.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Location</InputLabel>
            <Select
              value={filters.location_id}
              onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
              label="Location"
            >
              <MenuItem value="">All</MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => {
              fetchLogs();
              setShowFilters(false);
            }}
            fullWidth
          >
            Apply Filters
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              setFilters({
                start_date: null,
                end_date: null,
                user_id: '',
                individual_id: '',
                location_id: '',
                action_type: ''
              });
              fetchLogs();
            }}
            fullWidth
          >
            Clear Filters
          </Button>
        </Box>
      </Drawer>

      {/* Timeline/Ledger View */}
      {groupedLogs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary" variant="h6">
            No audit logs found for the selected criteria
          </Typography>
        </Box>
      ) : (
        groupedLogs.map(([dateKey, dayLogs]) => (
          <Box key={dateKey} sx={{ mb: 4 }}>
            {/* Sticky Date Header */}
            <Paper
              elevation={0}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'primary.main',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 1,
                mb: 2
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {formatDate(dateKey)}
              </Typography>
              <Typography variant="caption">
                {dayLogs.length} {dayLogs.length === 1 ? 'entry' : 'entries'}
              </Typography>
            </Paper>

            {/* Custom Timeline */}
            <Box sx={{ position: 'relative', pl: 4, ml: 6 }}>
              {/* Vertical line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: 'divider'
                }}
              />

              {dayLogs.map((log) => (
                <Box
                  key={log.id}
                  sx={{
                    position: 'relative',
                    pb: 2,
                    '&:last-child': { pb: 0 }
                  }}
                >
                  {/* Timeline dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 1,
                      top: 8,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: `${getActionColor(log.action)}.main`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      zIndex: 1,
                      '& svg': { fontSize: 14 }
                    }}
                  >
                    {getActionIcon(log.action)}
                  </Box>

                  {/* Time label */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      left: -55,
                      top: 10,
                      width: 50,
                      textAlign: 'right'
                    }}
                  >
                    {formatTime(log.timestamp)}
                  </Typography>

                  {/* Content card */}
                  <Card
                    variant="outlined"
                    sx={{
                      ml: 4,
                      cursor: (log.old_value || log.new_value) ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                    onClick={() => (log.old_value || log.new_value) && handleToggleCard(log.id)}
                  >
                    <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={log.action}
                              size="small"
                              color={getActionColor(log.action)}
                              variant="filled"
                            />
                            <Typography variant="body2" color="text.secondary">
                              by <strong>{log.username}</strong>
                            </Typography>
                          </Box>

                          {(log.individual_name || log.location_name || log.form_type) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {log.individual_name && (
                                <Chip
                                  icon={<PersonIcon />}
                                  label={log.individual_name}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {log.location_name && (
                                <Chip
                                  icon={<LocationIcon />}
                                  label={log.location_name}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {log.form_type && (
                                <Chip
                                  label={log.form_type}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              )}
                            </Box>
                          )}
                        </Box>

                        {(log.old_value || log.new_value) && (
                          <IconButton size="small">
                            {expandedCards[log.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </Box>

                      <Collapse in={expandedCards[log.id]}>
                        <Divider sx={{ my: 1.5 }} />
                        <Grid container spacing={2}>
                          {log.old_value && (
                            <Grid item xs={12} md={log.new_value ? 6 : 12}>
                              <Paper sx={{ p: 1.5, bgcolor: 'error.light', color: 'error.contrastText' }}>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                  Previous Value
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {log.old_value}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                          {log.new_value && (
                            <Grid item xs={12} md={log.old_value ? 6 : 12}>
                              <Paper sx={{ p: 1.5, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                  New Value
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {log.new_value}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                        </Grid>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default AuditLogViewer;
